import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

import { goToLink } from "@actions/common";
import { showAlert as showAlertAction } from "@components/alert/action";
import BetordoCasinoIcon from "@pages/casino/assets/backgrounds/betordo-casino-icon.webp";
import ChainspinCasinoIcon from "@pages/casino/assets/backgrounds/chainspin-casino-icon.webp";
import ChokwinCasinoIcon from "@pages/casino/assets/backgrounds/chokwin-casino-icon.webp";
import DefaultCasinoIcon from "@pages/casino/assets/backgrounds/default-casino-icon.png";
import FlashCasinoIcon from "@pages/casino/assets/backgrounds/flashsportscasino-casino-icon.webp";
import GannarCasinoIcon from "@pages/casino/assets/backgrounds/gannar-casino-icon.webp";
import LuckykingbetCasinoIcon from "@pages/casino/assets/backgrounds/luckykingbet-casino-icon.svg";
import MarsCasinoIcon from "@pages/casino/assets/backgrounds/mars-casino-icon.webp";
import NoveroCasinoIcon from "@pages/casino/assets/backgrounds/novero-casino-icon.webp";
import RollblockCasinoIcon from "@pages/casino/assets/backgrounds/rollblock-casino-icon.webp";
import ZigybetCasinoIcon from "@pages/casino/assets/backgrounds/zigybet-casino-icon.webp";
import { useMousePositionAnimation } from "@pages/casino/hooks";
import { openGame } from "@pages/casino/utils";

const useController = ({ isGamePage, game, type, hasPlayIconAndGameName }) => {
	useMousePositionAnimation("#play-overlay", game?.id);

	const dispatch = useDispatch();

	const redirect = useCallback((...args) => {
		dispatch(goToLink(...args));
	}, []);

	const showAlert = useCallback((...args) => {
		dispatch(showAlertAction(...args));
	}, []);

	const imageSrc = useMemo(() => {
		const iconHigh = game?.icon_high;
		const iconCustomUrl = game?.icon_custom_url;
		const gameIconUrl = game?.game_icon_url;

		if (iconHigh) return iconHigh;
		if (iconCustomUrl) return iconCustomUrl;
		if (gameIconUrl) return gameIconUrl;

		if (window.TENANT === "chokwin") {
			return ChokwinCasinoIcon;
		}

		if (window.TENANT === "zigybet") {
			return ZigybetCasinoIcon;
		}

		if (window.TENANT === "luckykingbet") {
			return LuckykingbetCasinoIcon;
		}

		if (window.TENANT === "flashsportscasino") {
			return FlashCasinoIcon;
		}

		if (window.TENANT === "betordo") {
			return BetordoCasinoIcon;
		}

		if (window.TENANT === "novero") {
			return NoveroCasinoIcon;
		}

		if (window.TENANT === "chainspin") {
			return ChainspinCasinoIcon;
		}

		if (window.TENANT === "rollblock") {
			return RollblockCasinoIcon;
		}

		if (window.TENANT === "gannar") {
			return GannarCasinoIcon;
		}

		if (hasPlayIconAndGameName) {
			return MarsCasinoIcon;
		}

		return DefaultCasinoIcon;
	}, [game?.icon_high, game?.icon_custom_url, game?.game_icon_url, hasPlayIconAndGameName]);

	const handleGameCardClick = useCallback(() => {
		if (isGamePage) {
			openGame(game, redirect, showAlert, {}, type);

			setTimeout(() => {
				window.location.reload();
			}, 500);
		} else {
			const { pathname, search } = window.location;

			const fullPath = `${pathname}${search}`;

			localStorage.setItem("casino.basePath", fullPath);
			openGame(game, redirect, showAlert, {}, type);
		}

		window?.dataLayer?.push({
			event: "casino-game-card-click",
			gameName: game?.remote_name,
			gameId: game?.game_id,
			gameAffiliate: game?.affiliate,
			gameProvider: game?.provider,
			is_live: game?.is_live,
		});
	}, [game, isGamePage, redirect, showAlert, type]);

	const handleImageError = useCallback(
		(event) => {
			event.target.src =
				window.TENANT === "chokwin"
					? ChokwinCasinoIcon
					: window.TENANT === "zigybet"
						? ZigybetCasinoIcon
						: window.TENANT === "luckykingbet"
							? LuckykingbetCasinoIcon
							: window.TENANT === "flashsportscasino"
								? FlashCasinoIcon
								: window.TENANT === "betordo"
									? BetordoCasinoIcon
									: window.TENANT === "novero"
										? NoveroCasinoIcon
										: window.TENANT === "chainspin"
											? ChainspinCasinoIcon
											: window.TENANT === "rollblock"
												? RollblockCasinoIcon
												: window.TENANT === "gannar"
													? GannarCasinoIcon
													: hasPlayIconAndGameName
														? MarsCasinoIcon
														: DefaultCasinoIcon;
		},
		[hasPlayIconAndGameName]
	);

	return {
		state: {
			imageSrc,
		},
		handlers: {
			handleImageError,
			handleGameCardClick,
		},
	};
};

export default useController;
